import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { PollForm, PollFormType } from './components/PollForm';

import { formatDateAsAnniversary } from 'helpers/dateTimes';

import { useCreatePollMutation, TotadminPermission } from 'data/__generated__';

export const PollCreate = () => {
    const navigate = useNavigate();

    const [createPoll, { loading: creationLoading }] = useCreatePollMutation();

    const initialForm = {
        audience: { organizationIds: [], siteIds: [], userIds: [] },
        answerOptions: [],
        dateWindow: { startDate: null, endDate: null },
        image: null,
        imageUrl: null,
        question: null,
        sectionTitle: null,
        title: null,
    };
    const [pollForm, setPollForm] = useState<PollFormType>(initialForm);

    async function onSubmit() {
        if (!pollForm.question) {
            toast.error('Question non renseignée.');
            return;
        }
        if (!pollForm.dateWindow.startDate || !pollForm.dateWindow.endDate) {
            toast.error('Date non renseignée.');
            return;
        }
        if (!pollForm.title) {
            toast.error('Titre non renseigné.');
            return;
        }
        const { data } = await createPoll({
            variables: {
                input: {
                    audience: pollForm.audience,
                    answerOptions: pollForm.answerOptions,
                    dateWindow: {
                        startDate: formatDateAsAnniversary({
                            dateTime: pollForm.dateWindow.startDate,
                        }),
                        endDate: formatDateAsAnniversary({ dateTime: pollForm.dateWindow.endDate }),
                    },
                    image: pollForm.image,
                    imageUrl: pollForm.imageUrl,
                    question: pollForm.question,
                    title: pollForm.title,
                },
            },
        });

        if (data) {
            const {
                createPoll: { poll, errors },
            } = data;
            if (poll) {
                toast.success('Le sondage a bien été créé !');
                navigate(`/poll/${poll._id}`);
            } else {
                if (errors) {
                    errors.forEach((error) => {
                        toast.error(error, { autoClose: false });
                    });

                    if (errors.length > 1) {
                        toast.info('Cliquez pour fermer toutes les notifications', {
                            autoClose: false,
                            onClick: () => toast.dismiss(),
                        });
                    }
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    }

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/polls' }],
        },
    ];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.pollCreate} />
                </HeaderTitle>
                <ActionsAndSave
                    actionGroups={actionGroups}
                    onSave={onSubmit}
                    saveLoading={creationLoading}
                    saveTitle="Créer"
                    saveDataTest="create-button"
                    saveRequiredPermissions={[TotadminPermission.PollsManage]}
                />
            </Header>
            <Content>
                <ScrollableContent>
                    <PollForm pollForm={pollForm} setPollForm={setPollForm} />
                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
